import { ActionIcon, Group, SegmentedControl, Stack, Text, Tooltip } from '@mantine/core';
import { IconType } from 'react-icons';
import { type NodeProps, useStore as useReactFlowStore } from '@xyflow/react';
import { BsDash } from 'react-icons/bs';
import { IoMdCheckmark } from 'react-icons/io';
import { IoCloseSharp } from 'react-icons/io5';
import { PiCodepenLogo } from 'react-icons/pi';
import { match } from 'ts-pattern';
import { GraphNodeOutput } from '../graph-node-output';
import {
  EvaluationValue,
  EvaluationValueKind,
  NodeEvaluationStatus,
  NodeId,
  NodeInstance,
} from '../../types';
import { FiTrash2 } from 'react-icons/fi';
import { TbReplace } from 'react-icons/tb';
import { IoMdRefresh } from 'react-icons/io';
import { FiPlay } from 'react-icons/fi';
import { TbArrowUp, TbArrowDown } from 'react-icons/tb';
import { PortConfig, useLogicBlocks } from '@morph-mapper/node-logic';
import { SchemaVariant } from '@morph-mapper/types';
import { RFGraphNode } from '../../organisms/graph-view/utils';
import { useGraphContext } from '../../context';
import { DrawerKind, NodePickerContextType } from '../../store/ui';
import {
  createRemoveNodeCommand,
  createUpdateNodeOptionValueCommand,
  SwapEdgeOrderCommand,
} from '../../lib/commands';
import { useStore } from '@/store';
import { Handle } from '../../atoms';
import { t } from 'i18next';
import { formatEvaluationValue } from '../../utils';
import {
  NodeActions,
  NodeBody,
  NodeContainer,
  NodeFooter,
  NodeHeader,
  NodeHeaderInfo,
  NodeHeaderIcon,
  NodeHeaderRow,
  NodeHeaderTitle,
} from './styles';

type InlineOption = {
  key: string;
  value: string;
  selection: { label: string; value: string }[];
};

export interface GraphNodeProps {
  id: NodeId;
  icon: IconType;
  title: string;
  treeIndex?: string;
  isActive?: boolean;
  issueCount?: number;
  derivationCount?: number;
  ports: PortConfig;
  inlineOptions?: InlineOption[];
  onDerivationsClick: () => void;
  onStatusClick?: () => void;
  onRemove?: () => void;
  onReplace?: () => void;
  onRun?: () => void;
  onInlineOptionChange?: (key: string, value: string) => void;
}

export type GraphNodeData = GraphNodeProps;

const GraphNodeHandles = ({ id, ports }: { id: NodeId; ports: PortConfig }) => {
  const [openDrawer, setFocusNodeId] = useGraphContext(({ ui }) => [
    ui.openDrawer,
    ui.setFocusNodeId,
  ]);
  const edges = useReactFlowStore((state) => state.edges);

  return (
    <>
      {ports.input.map((port, index) => {
        const hasConnection = edges.some(
          (edge) => edge.target === id && edge.targetHandle === port.id
        );
        const showAdd = port.multi ? true : !hasConnection;
        return (
          <Handle
            key={`input-${port.id}`}
            index={index}
            total={ports.input.length}
            port={port}
            showAdd={showAdd}
            onAdd={() => {
              setFocusNodeId(id);
              openDrawer({
                kind: DrawerKind.NodePicker,
                context: {
                  mode: NodePickerContextType.AddChildToNode,
                  nodeId: id,
                  portId: port.id,
                },
              });
            }}
          />
        );
      })}
      {ports.output.map((port, index) => (
        <Handle
          key={`output-${port.id}`}
          index={index}
          total={ports.output.length}
          port={port}
        />
      ))}
    </>
  );
};

const GraphNodeHeader = ({
  icon: IconNodeType,
  title,
  treeIndex,
  onRemove,
  onReplace,
  onRun,
}: {
  icon: IconType;
  title: string;
  treeIndex?: string;
  onRemove?: () => void;
  onReplace?: () => void;
  onRun?: () => void;
}) => {
  return (
    <NodeHeader>
      <NodeHeaderRow>
        <NodeHeaderInfo>
          <NodeHeaderIcon size="sm" variant="default">
            <IconNodeType />
          </NodeHeaderIcon>
          <NodeHeaderTitle>
            {treeIndex && treeIndex !== '0' && (
              <Text fz={9} fw={600} c="dimmed" ff="monospace" lh={1}>
                {treeIndex}
              </Text>
            )}
            <Text fw={600} fz="sm" lineClamp={1}>
              {t(title)}
            </Text>
          </NodeHeaderTitle>
        </NodeHeaderInfo>

        <NodeActions>
          <div className="graph-node-actions">
            <Tooltip label={t('graphEditor.node.replace')} withArrow>
              <ActionIcon
                variant="subtle"
                radius="md"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  onReplace?.();
                }}
                aria-label={t('graphEditor.node.replace')}
              >
                <TbReplace size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={t('graphEditor.node.run')} withArrow>
              <ActionIcon
                variant="subtle"
                radius="md"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  onRun?.();
                }}
                aria-label={t('graphEditor.node.run')}
              >
                <FiPlay size={14} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={t('graphEditor.node.remove')} withArrow>
              <ActionIcon
                variant="subtle"
                radius="md"
                size="sm"
                color="red"
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove?.();
                }}
                aria-label={t('graphEditor.node.remove')}
              >
                <FiTrash2 size={14} />
              </ActionIcon>
            </Tooltip>
          </div>
        </NodeActions>
      </NodeHeaderRow>
    </NodeHeader>
  );
};

const getOptionValue = (node: NodeInstance, optionId: string) => {
  const entry = node.options[optionId];
  return entry && 'value' in entry ? entry.value : undefined;
};

const getConnectedInputLabel = (
  node: NodeInstance | undefined,
  evaluation: {
    getNodeStatus: (id: NodeId) => NodeEvaluationStatus;
    getNodeValue: (id: NodeId) => EvaluationValue;
  },
  logicBlocks: ReturnType<typeof useLogicBlocks>['logicBlocks']
): string => {
  if (!node) return '';

  if (evaluation.getNodeStatus(node.id) === NodeEvaluationStatus.Ok) {
    const value = evaluation.getNodeValue(node.id);
    if (value.kind !== EvaluationValueKind.Empty) {
      return formatEvaluationValue(value);
    }
  }

  if (node.kind === 'literal') {
    const value = getOptionValue(node, 'literal');
    if (value === undefined || value === '') return t('graphEditor.node.emptyValue');
    return String(value);
  }

  return t(logicBlocks[node.kind]?.title ?? node.kind);
};

const GraphNodeInputOrder = ({ id, ports }: { id: NodeId; ports: PortConfig }) => {
  const multiPorts = ports.input.filter((p) => p.multi);
  const edges = useReactFlowStore((state) => state.edges);
  const applyCommand = useGraphContext(({ cmd }) => cmd.applyCommand);
  const isLayoutRunning = useGraphContext(({ ui }) => ui.isLayoutRunning);
  const setFocusNodeId = useGraphContext(({ ui }) => ui.setFocusNodeId);
  const nodes = useGraphContext(({ graph: g }) => g.getNodes());
  const evaluation = useGraphContext(({ eval: ev }) => ev);
  const variant =
    useStore((state) => state.config.templateVariant) ?? SchemaVariant.Json;
  const { logicBlocks } = useLogicBlocks(variant);

  const connectedEdges = multiPorts.flatMap((port) =>
    edges
      .filter((e) => e.target === id && e.targetHandle === port.id)
      .sort((a, b) => ((a.data?.order as number) ?? 0) - ((b.data?.order as number) ?? 0))
  );

  if (connectedEdges.length < 2) return null;

  const swap = (indexA: number, indexB: number) => {
    setFocusNodeId(id);
    applyCommand(new SwapEdgeOrderCommand(connectedEdges[indexA].id, connectedEdges[indexB].id));
  };

  return (
    <NodeBody>
      <Stack spacing={4}>
        <Text fz="xs" c="dimmed" fw={600}>
          {t('graphEditor.node.inputsLabel')}
        </Text>
        {connectedEdges.map((edge, index) => (
          <Group key={edge.id} position="apart" spacing={4} noWrap>
            <Group spacing={4} noWrap sx={{ minWidth: 0, flex: 1 }}>
              <Text fz="xs" c="dimmed">{index + 1}</Text>
              <Text fz="xs" lineClamp={1} sx={{ minWidth: 0 }}>
                {getConnectedInputLabel(nodes[edge.source], evaluation, logicBlocks)}
              </Text>
            </Group>
            <Group spacing={2}>
              <ActionIcon
                size="xs"
                variant="subtle"
                disabled={isLayoutRunning || index === 0}
                onClick={(e) => { e.stopPropagation(); swap(index, index - 1); }}
              >
                <TbArrowUp size={10} />
              </ActionIcon>
              <ActionIcon
                size="xs"
                variant="subtle"
                disabled={isLayoutRunning || index === connectedEdges.length - 1}
                onClick={(e) => { e.stopPropagation(); swap(index, index + 1); }}
              >
                <TbArrowDown size={10} />
              </ActionIcon>
            </Group>
          </Group>
        ))}
      </Stack>
    </NodeBody>
  );
};

const GraphNodeBody = ({
  id,
  inlineOptions,
  onInlineOptionChange,
}: {
  id: NodeId;
  inlineOptions?: InlineOption[];
  onInlineOptionChange?: (key: string, value: string) => void;
}) => {
  return (
    <NodeBody>
      <Stack spacing={6}>
        {inlineOptions?.map(({ key, value, selection }) => (
          <SegmentedControl
            key={key}
            size="xs"
            value={value}
            data={selection}
            onChange={(v) => onInlineOptionChange?.(key, v)}
            onClick={(e) => e.stopPropagation()}
          />
        ))}
        <Text fz="xs" c="dimmed" fw={600}>
          {t('graphEditor.node.outputLabel')}
        </Text>
        <GraphNodeOutput id={id} />
      </Stack>
    </NodeBody>
  );
};

const GraphNodeFooter = ({
  issueCount,
  derivationCount,
  status,
  onDerivationsClick,
  onStatusClick,
}: {
  issueCount: number;
  derivationCount: number;
  status: NodeEvaluationStatus;
  onDerivationsClick: () => void;
  onStatusClick?: () => void;
}) => {
  const hasDerivations = derivationCount > 0;
  const hasIssues = issueCount > 0;

  return (
    <NodeFooter>
      <Group position="apart">
        <Group spacing="xs" align="center">
          {hasDerivations && (
            <>
              <Tooltip label={t('graphEditor.node.viewDerivations')} withArrow>
                <ActionIcon
                  sx={{ border: 'none' }}
                  color="orange"
                  variant="outline"
                  size="xs"
                  radius="xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDerivationsClick();
                  }}
                  aria-label={t('graphEditor.node.viewDerivations')}
                >
                  <PiCodepenLogo />
                </ActionIcon>
              </Tooltip>
              <Text fz="xs" c="dimmed" fw={500}>
                {derivationCount}
              </Text>
            </>
          )}
          {hasIssues && (
            <Text fz="xs" c="dimmed" fw={500}>
              {issueCount}
            </Text>
          )}
        </Group>

        {match(status)
          .with(NodeEvaluationStatus.Ok, () => (
            <Tooltip label={t('graphEditor.node.viewStatus')} withArrow>
              <ActionIcon
                color="green"
                variant="outline"
                size="xs"
                radius="sm"
                onClick={onStatusClick}
                aria-label={t('graphEditor.node.viewStatus')}
                sx={{ border: 'none' }}
              >
                <IoMdCheckmark />
              </ActionIcon>
            </Tooltip>
          ))
          .with(NodeEvaluationStatus.Idle, () => (
            <Tooltip label={t('graphEditor.node.noStatus')} withArrow>
              <ActionIcon
                color="gray"
                variant="light"
                size="xs"
                radius="sm"
                onClick={onStatusClick}
                aria-label={t('graphEditor.node.noStatus')}
              >
                <BsDash />
              </ActionIcon>
            </Tooltip>
          ))
          .with(NodeEvaluationStatus.Running, () => (
            <Tooltip label={t('graphEditor.node.running')} withArrow>
              <ActionIcon
                color="blue"
                variant="outline"
                size="xs"
                radius="sm"
                onClick={onStatusClick}
                aria-label={t('graphEditor.node.running')}
                sx={{ border: 'none' }}
              >
                <IoMdRefresh />
              </ActionIcon>
            </Tooltip>
          ))
          .with(NodeEvaluationStatus.Error, () => (
            <Tooltip label={t('graphEditor.node.viewStatus')} withArrow>
              <ActionIcon
                color="red"
                variant="outline"
                size="xs"
                radius="sm"
                onClick={onStatusClick}
                aria-label={t('graphEditor.node.viewStatus')}
                sx={{ border: 'none' }}
              >
                <IoCloseSharp />
              </ActionIcon>
            </Tooltip>
          ))
          .exhaustive()}
      </Group>
    </NodeFooter>
  );
};

export const GraphNode = ({
  id,
  icon: IconNodeType,
  title,
  treeIndex,
  isActive = false,
  issueCount = 0,
  derivationCount = 0,
  ports,
  inlineOptions,
  onDerivationsClick,
  onStatusClick,
  onRemove,
  onReplace,
  onRun,
  onInlineOptionChange,
}: GraphNodeProps) => {
  const status = useGraphContext(({ eval: ev }) => ev.getNodeStatus(id));

  return (
    <NodeContainer data-graph-node tabIndex={0} isSelected={isActive}>
      <GraphNodeHandles id={id} ports={ports} />
      <Stack spacing="none">
        <GraphNodeHeader
          icon={IconNodeType}
          title={title}
          treeIndex={treeIndex}
          onRemove={onRemove}
          onReplace={onReplace}
          onRun={onRun}
        />
        <GraphNodeInputOrder id={id} ports={ports} />
        <GraphNodeBody
          id={id}
          inlineOptions={inlineOptions}
          onInlineOptionChange={onInlineOptionChange}
        />
        <GraphNodeFooter
          issueCount={issueCount}
          derivationCount={derivationCount}
          status={status}
          onDerivationsClick={onDerivationsClick}
          onStatusClick={onStatusClick}
        />
      </Stack>
    </NodeContainer>
  );
};

export const GenericGraphNodeFlow = ({ data }: NodeProps<RFGraphNode>) => {
  const { kind, nodeId } = data;
  const [openDrawer, applyCommand, enqueueGraph, activeNodeId, setFocusNodeId, INode] =
    useGraphContext(({ ui, cmd, graph: g }) => [
      ui.openDrawer,
      cmd.applyCommand,
      g.enqueueGraph,
      ui.drawer.kind === DrawerKind.NodeConfig ? ui.drawer.nodeId : undefined,
      ui.setFocusNodeId,
      g.node(nodeId),
    ]);
  // TODO: Where do we get the properties from
  const variant =
    useStore((state) => state.config.templateVariant) ?? SchemaVariant.Json;
  const { logicBlocks } = useLogicBlocks(variant);
  const nodeValidation = useGraphContext(
    (state) => state.valid.validation?.nodes[nodeId]
  );
  const derivationCount = useGraphContext((state) =>
    state.graph.getNodeDerivationCount(nodeId)
  );

  const block = logicBlocks[kind];
  const { icon, title, ports } = block;
  const { treeIndex } = data;

  // Options marked isInline are also shown on the node, e.g. In Array's
  // Any value / All values.
  const currentOptions = INode.getOptions();
  const inlineOptions = Object.entries(block.options ?? {})
    .filter(([, option]) => option.conditions?.isInline && option.selection)
    .map(([key, option]) => {
      const entry = currentOptions[key];
      const current = entry && 'value' in entry ? entry.value : undefined;
      return {
        key,
        value: String(current ?? option.default),
        selection: (option.selection ?? []).map(
          ({ label, value }: { label: string; value: unknown }) => ({
            label,
            value: String(value),
          })
        ),
      };
    });

  const handleInlineOptionChange = (key: string, value: string) => {
    applyCommand(createUpdateNodeOptionValueCommand(nodeId, key, value));
  };

  const handleReplace = () => {
    setFocusNodeId(nodeId);
    openDrawer({
      kind: DrawerKind.NodePicker,
      context: {
        mode: NodePickerContextType.ReplaceNode,
        nodeId,
      },
    });
  };

  const handleRemove = () => {
    const result = applyCommand(createRemoveNodeCommand(nodeId));
    if (result.isErr()) {
      console.warn('Failed to remove node.', result.error);
    }
  };

  const handleRun = () => {
    enqueueGraph([nodeId]);
  };

  const handleDerivations = () => {
    openDrawer({ kind: DrawerKind.NodeConfig, nodeId, tab: 'derivations' });
  };

  return (
    <GraphNode
      id={nodeId}
      icon={icon}
      title={title}
      treeIndex={treeIndex}
      isActive={activeNodeId === nodeId}
      issueCount={nodeValidation?.issueIds.length ?? 0}
      derivationCount={derivationCount}
      ports={ports}
      inlineOptions={inlineOptions.length > 0 ? inlineOptions : undefined}
      onReplace={handleReplace}
      onRemove={handleRemove}
      onRun={handleRun}
      onDerivationsClick={handleDerivations}
      onInlineOptionChange={handleInlineOptionChange}
    />
  );
};
